import { getCurrentLocale, getI18n } from '@/i18n'

type MarketLabelSource = {
  slug?: string
  name?: string
  description?: string
  outcomes?: Array<{ specifier?: string | null }>
}

/** Fallback when `lang` header is missing or DB has no market_translation row. */
const MARKET_SLUG_TERMS_KEY: Record<string, string> = {
  'standard-bet-without-bonus': 'terms.standardBetNoBonus',
  'standard-bet-incl-bonus-from-same-drum': 'terms.standardBetSameDrum',
  'standard-bet-bonus-from-same-drum': 'terms.standardBetSameDrum',
  'standard-bet-incl-bonus-from-additional-drum': 'terms.standardBetAdditionalDrum',
  'standard-bet-bonus-from-additional-drum': 'terms.standardBetAdditionalDrum',
  'standard-bet-excl-bonus-plus-color-bonus': 'terms.standardBetNoBonus',
}

/** Default English catalog names — if API still returns these, use locale JSON instead. */
const CANONICAL_ENGLISH_NAME_BY_SLUG: Record<string, string> = {
  'standard-bet-without-bonus': 'Standard bet (without bonus)',
  'standard-bet-incl-bonus-from-same-drum': 'Standard bet (incl. bonus from same drum)',
  'standard-bet-bonus-from-same-drum': 'Standard bet (incl. bonus from same drum)',
  'standard-bet-incl-bonus-from-additional-drum': 'Standard bet (incl. bonus from additional drum)',
  'standard-bet-bonus-from-additional-drum': 'Standard bet (incl. bonus from additional drum)',
}

const applySpecifierToDescription = (description: string, specifier?: string | null): string => {
  if (!description) return ''
  if (description.includes('{value}') && specifier) {
    return description.replace('{value}', specifier)
  }
  return description
}

const localizedLabelFromSlug = (slug: string): string | null => {
  const termsKey = MARKET_SLUG_TERMS_KEY[slug.trim().toLowerCase()]
  if (!termsKey) return null

  const i18n = getI18n()
  if (!i18n?.global?.te?.(termsKey)) return null

  return String(i18n.global.t(termsKey))
}

/**
 * Label shown in market selectors and headers.
 * Prefer API `name` (includes account-specific alt_name when enabled).
 */
export const getMarketDisplayLabel = (market: MarketLabelSource): string => {
  const name = (market.name || '').trim()
  const locale = getCurrentLocale()
  const slug = (market.slug || '').trim()

  if (name) {
    if (locale !== 'en' && slug) {
      const canonical = CANONICAL_ENGLISH_NAME_BY_SLUG[slug.toLowerCase()]
      if (canonical && name.localeCompare(canonical, 'en', { sensitivity: 'accent' }) === 0) {
        const fromSlug = localizedLabelFromSlug(slug)
        if (fromSlug) return fromSlug
      }
    }
    return name
  }

  if (locale !== 'en' && slug) {
    const fromSlug = localizedLabelFromSlug(slug)
    if (fromSlug) return fromSlug
  }

  const specifier = market.outcomes?.[0]?.specifier
  return applySpecifierToDescription((market.description || '').trim(), specifier)
}
